import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';
import { Btn, Chip, Modal, InputField } from '@/presentation/component/ui';
import { inputClass } from '@/presentation/component/formStyles';
import type { AdminCourse } from '@/domain/model/AdminCourse';
import { detectMediaType } from '@/domain/model/MediaType';
import { extractMediaDuration } from '@/presentation/util/mediaDuration';
import { ACCEPTED_MEDIA_TYPES, mediaTypeLabelKey } from '@/presentation/util/mediaType';
import { documentFileKey } from '@/presentation/util/documentType';
import { isPdf, type CourseDocument } from '@/domain/model/CourseDocument';
import { formatDuration } from '@/presentation/util/format';
import { CourseDocumentsField } from './CourseDocumentsField';
import type { AdminChapter } from '@/domain/model/AdminChapter';
import type { CourseUploadFiles } from '@/domain/model/CourseUploadFiles';

export interface CourseFormData {
  title: string;
  description: string;
  duration: number;
  chapterId: string;
  displayOrder?: number;
  preview: boolean;
}

const EMPTY_FORM: CourseFormData = {
  title: '',
  description: '',
  duration: 0,
  chapterId: '',
  preview: false,
};

const addNewDocuments = (current: File[], chosen: File[]): File[] => {
  const currentKeys = new Set(current.map(documentFileKey));
  return [...current, ...chosen.filter((file) => !currentKeys.has(documentFileKey(file)))];
};

interface DescriptionFieldProps {
  value: string;
  onChange: (value: string) => void;
}

const DescriptionField = ({ value, onChange }: DescriptionFieldProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor="course-description"
        className="text-sm font-medium text-[var(--color-ink-soft)]"
      >
        {t('admin.courseDescription')}
      </label>
      <textarea
        id="course-description"
        className={inputClass()}
        placeholder={t('admin.courseDescriptionPlaceholder')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        required
      />
    </div>
  );
};

interface MediaUploadProps {
  editingCourse: AdminCourse | null;
  mediaFile: File | null;
  error: string | null;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const MediaUpload = ({
  editingCourse,
  mediaFile,
  error,
  fileInputRef,
  onFileChange,
}: MediaUploadProps) => {
  const { t } = useTranslation();
  const mediaType = mediaFile ? detectMediaType(mediaFile.type) : null;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor="course-media" className="text-sm font-medium text-[var(--color-ink-soft)]">
        {t('admin.mediaFile')}{' '}
        {!editingCourse && <span className="text-[var(--color-bad)]">*</span>}
      </label>
      <div className="flex items-center gap-3">
        <label
          htmlFor="course-media"
          className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[var(--color-line-strong)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-ink)] transition-colors hover:bg-[var(--color-surface-2)]"
        >
          <Upload className="h-4 w-4" />
          {t('admin.chooseFile')}
        </label>
        <input
          ref={fileInputRef}
          id="course-media"
          type="file"
          accept={ACCEPTED_MEDIA_TYPES}
          className="sr-only"
          onChange={onFileChange}
          aria-invalid={error !== null}
          aria-describedby={error ? 'course-media-error' : undefined}
        />
        {mediaFile && (
          <span className="truncate text-sm text-[var(--color-ink-soft)]">{mediaFile.name}</span>
        )}
        {mediaType && <Chip kind="accent">{t(mediaTypeLabelKey(mediaType))}</Chip>}
        {editingCourse && !mediaFile && (
          <span className="text-sm text-[var(--color-ink-faint)]">
            {t('admin.keepCurrentMedia')}
          </span>
        )}
      </div>
      {error && (
        <span id="course-media-error" role="alert" className="text-sm text-[var(--color-bad)]">
          {error}
        </span>
      )}
    </div>
  );
};

interface UploadProgressBarProps {
  progress: number;
}

const UploadProgressBar = ({ progress }: UploadProgressBarProps) => {
  const { t } = useTranslation();
  const isFinalizing = progress === 100;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between text-sm text-[var(--color-ink-soft)]">
        <span>{isFinalizing ? t('admin.finalizing') : t('admin.uploading')}</span>
        <span>{progress}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--color-surface-2)]">
        <div
          className={`h-full rounded-full bg-[var(--color-accent)] transition-all ${
            isFinalizing ? 'animate-pulse' : ''
          }`}
          style={{ width: `${progress}%` }}
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
};

interface DurationFieldProps {
  duration: number;
  autoDuration: boolean;
  onChange: (duration: number) => void;
}

const DurationField = ({ duration, autoDuration, onChange }: DurationFieldProps) => {
  const { t } = useTranslation();

  if (autoDuration) {
    return (
      <InputField
        label={t('admin.duration')}
        value={formatDuration(duration)}
        readOnly
        className="cursor-default bg-[var(--color-surface-2)] text-[var(--color-ink-faint)]"
      />
    );
  }

  return (
    <InputField
      label={t('admin.duration')}
      type="number"
      min={0}
      placeholder={t('admin.durationPlaceholder')}
      value={duration}
      onChange={(e) => onChange(Number(e.target.value))}
      required
    />
  );
};

interface ChapterSelectProps {
  value: string;
  chapters: AdminChapter[];
  onChange: (chapterId: string) => void;
}

const ChapterSelect = ({ value, chapters, onChange }: ChapterSelectProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor="course-chapter" className="text-sm font-medium text-[var(--color-ink-soft)]">
        {t('admin.parentChapter')} <span className="text-[var(--color-bad)]">*</span>
      </label>
      <select
        id="course-chapter"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass()}
        required
      >
        <option value="">{t('admin.parentChapter')}</option>
        {chapters.map((ch) => (
          <option key={ch.id} value={ch.id}>
            {ch.name}
          </option>
        ))}
      </select>
    </div>
  );
};

interface CourseFormModalProps {
  open: boolean;
  editingCourse: AdminCourse | null;
  chapters: AdminChapter[];
  isLoading: boolean;
  uploadProgress: number | null;
  onSubmit: (data: CourseFormData, files: CourseUploadFiles) => void;
  onRemoveDocument: (documentId: string) => Promise<boolean>;
  onClose: () => void;
}

export const CourseFormModal = ({
  open,
  editingCourse,
  chapters,
  isLoading,
  uploadProgress,
  onSubmit,
  onRemoveDocument,
  onClose,
}: CourseFormModalProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CourseFormData>(EMPTY_FORM);
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [documentFiles, setDocumentFiles] = useState<File[]>([]);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [savedDocuments, setSavedDocuments] = useState<CourseDocument[]>([]);
  const [autoDuration, setAutoDuration] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const latestFileRef = useRef<File | null>(null);
  const isUploading = uploadProgress !== null;

  const resetFiles = useCallback(() => {
    setMediaFile(null);
    setMediaError(null);
    setDocumentFiles([]);
    setDocumentError(null);
    latestFileRef.current = null;
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (documentInputRef.current) documentInputRef.current.value = '';
  }, []);

  useEffect(() => {
    if (open) {
      const isEdit = editingCourse !== null;
      setFormData(
        isEdit
          ? {
              title: editingCourse.title,
              description: editingCourse.description,
              duration: editingCourse.duration,
              chapterId: editingCourse.chapterId,
              displayOrder: editingCourse.displayOrder,
              preview: editingCourse.preview,
            }
          : EMPTY_FORM,
      );
      resetFiles();
      setSavedDocuments(editingCourse?.documents ?? []);
      setAutoDuration(isEdit);
    }
  }, [open, editingCourse, resetFiles]);

  const handleClose = () => {
    setFormData(EMPTY_FORM);
    resetFiles();
    setAutoDuration(false);
    onClose();
  };

  const rejectUnsupportedFile = () => {
    setMediaError(t('admin.unsupportedMediaFormat'));
    setMediaFile(null);
    setAutoDuration(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const applyDetectedDuration = async (file: File) => {
    try {
      const duration = await extractMediaDuration(file);
      if (latestFileRef.current !== file) return;
      setFormData((prev) => ({ ...prev, duration }));
      setAutoDuration(true);
    } catch {
      if (latestFileRef.current !== file) return;
      setAutoDuration(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    latestFileRef.current = file;
    if (file && detectMediaType(file.type) === null) {
      rejectUnsupportedFile();
      return;
    }
    setMediaError(null);
    setMediaFile(file);
    if (!file) {
      setAutoDuration(false);
      return;
    }
    await applyDetectedDuration(file);
  };

  const handleDocumentsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosenFiles = Array.from(e.target.files ?? []);
    if (documentInputRef.current) documentInputRef.current.value = '';
    if (chosenFiles.length === 0) return;
    if (chosenFiles.some((file) => !isPdf(file.type))) {
      setDocumentError(t('admin.unsupportedDocumentFormat'));
      return;
    }
    setDocumentError(null);
    setDocumentFiles((previous) => addNewDocuments(previous, chosenFiles));
  };

  const handleRemoveSelectedDocument = (file: File) => {
    setDocumentFiles((previous) =>
      previous.filter((selected) => documentFileKey(selected) !== documentFileKey(file)),
    );
  };

  const handleRemoveSavedDocument = async (documentId: string) => {
    const isRemoved = await onRemoveDocument(documentId);
    if (!isRemoved) return;
    setSavedDocuments((previous) => previous.filter((saved) => saved.id !== documentId));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCourse && !mediaFile) return;
    onSubmit(formData, { media: mediaFile ?? undefined, documents: documentFiles });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={editingCourse ? t('admin.editCourse') : t('admin.addCourse')}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <InputField
          label={t('admin.courseTitle')}
          placeholder={t('admin.courseTitlePlaceholder')}
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
        />
        <DescriptionField
          value={formData.description}
          onChange={(description) => setFormData({ ...formData, description })}
        />
        <MediaUpload
          editingCourse={editingCourse}
          mediaFile={mediaFile}
          error={mediaError}
          fileInputRef={fileInputRef}
          onFileChange={handleFileChange}
        />
        <CourseDocumentsField
          savedDocuments={savedDocuments}
          selectedFiles={documentFiles}
          error={documentError}
          isRemoveDisabled={isLoading}
          documentInputRef={documentInputRef}
          onFilesChange={handleDocumentsChange}
          onRemoveSaved={handleRemoveSavedDocument}
          onRemoveSelected={handleRemoveSelectedDocument}
        />
        {isUploading && <UploadProgressBar progress={uploadProgress} />}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DurationField
            duration={formData.duration}
            autoDuration={autoDuration}
            onChange={(duration) => setFormData({ ...formData, duration })}
          />
          {editingCourse && (
            <InputField
              label={t('admin.displayOrder')}
              type="number"
              min={0}
              value={formData.displayOrder ?? 0}
              onChange={(e) => setFormData({ ...formData, displayOrder: Number(e.target.value) })}
              required
            />
          )}
        </div>
        <ChapterSelect
          value={formData.chapterId}
          chapters={chapters}
          onChange={(chapterId) => setFormData({ ...formData, chapterId })}
        />
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.preview}
            onChange={(e) => setFormData({ ...formData, preview: e.target.checked })}
            className="h-4 w-4 rounded border-[var(--color-line-strong)] accent-[var(--color-accent)]"
          />
          <span className="text-sm font-medium text-[var(--color-ink-soft)]">
            {t('admin.coursePreview')}
          </span>
          <span className="text-xs text-[var(--color-ink-faint)]">
            {t('admin.coursePreviewHint')}
          </span>
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Btn kind="ghost" type="button" onClick={handleClose}>
            {t('admin.cancel')}
          </Btn>
          <Btn kind="primary" type="submit" disabled={isLoading} isLoading={isLoading}>
            {t('admin.save')}
          </Btn>
        </div>
      </form>
    </Modal>
  );
};
